import { Plus, ArrowRight, ChevronUp, ChevronDown, MoreVertical, Star } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

function WalletManager({
  wallets,
  isCompactMobileList = false,
  onAdd,
  onEdit,
  onDelete,
  onTransfer,
  onShowForm,
  onEditWallet,
  onShowTransferModal,
  onWalletClick,
  isSorting = false,
  onToggleSorting,
  onMoveWallet,
  onToggleFavorite
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Gestione Conti</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onToggleSorting?.()}
            aria-pressed={isSorting}
            title="Riordina conti"
            className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all duration-200 ${isSorting
                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700'
              }`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          <button
            onClick={onShowForm}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600/90 backdrop-blur-sm text-white rounded-xl shadow-lg hover:bg-blue-700/90 transition-all duration-200 transform hover:scale-105"
          >
            <Plus className="w-4 h-4" />
            <span className="font-medium">Nuovo</span>
          </button>
        </div>
      </div>
      <div className={`grid grid-cols-1 gap-3 ${isCompactMobileList ? 'max-h-[260px] overflow-y-auto pr-1 -mr-1' : ''}`}>
        {wallets
          .filter(wallet => wallet.balance <= -0.01 || wallet.balance >= 0.01)
          .map((wallet, index) => (
            <div
              key={wallet.id}
              className={`card px-3 py-3 flex items-center justify-between gap-2 transition-all duration-200 transform ${isSorting
                  ? 'cursor-default'
                  : 'cursor-pointer hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                }`}
              onClick={!isSorting ? () => onWalletClick(wallet) : undefined}
              role={isSorting ? 'group' : undefined}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleFavorite?.(wallet.id);
                  }}
                  disabled={isSorting}
                  aria-pressed={wallet.isFavorite}
                  aria-label={wallet.isFavorite ? 'Rimuovi dai preferiti' : 'Imposta come preferito'}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${wallet.isFavorite
                      ? 'text-yellow-500 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/40'
                      : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:text-gray-500 dark:hover:text-yellow-300/80 dark:hover:bg-yellow-900/20'
                    } ${isSorting ? 'cursor-default opacity-70' : ''}`}
                >
                  <Star
                    className="w-4 h-4"
                    strokeWidth={wallet.isFavorite ? 0 : 1.5}
                    fill={wallet.isFavorite ? 'currentColor' : 'none'}
                  />
                </button>
                <span className="w-4 h-4 rounded-full" style={{ background: wallet.color, display: 'inline-block' }}></span>
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{wallet.name}</div>
                  {wallet.type && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{wallet.type}</div>
                  )}
                </div>
              </div>
              {isSorting ? (
                <div className="flex items-center gap-1 ml-2">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onMoveWallet?.(wallet.id, 'up');
                    }}
                    disabled={index === 0}
                    aria-label="Sposta in alto"
                    className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${index === 0
                        ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                        : 'bg-white/70 hover:bg-gray-100 border-gray-200 dark:bg-gray-800/60 dark:hover:bg-gray-700 dark:border-gray-700'
                      }`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onMoveWallet?.(wallet.id, 'down');
                    }}
                    disabled={index === wallets.length - 1}
                    aria-label="Sposta in basso"
                    className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${index === wallets.length - 1
                        ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                        : 'bg-white/70 hover:bg-gray-100 border-gray-200 dark:bg-gray-800/60 dark:hover:bg-gray-700 dark:border-gray-700'
                      }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={`ml-2 flex-shrink-0 px-3 py-0.5 rounded-xl border font-bold text-base ${wallet.balance > 0
                      ? 'text-green-700 border-green-200 bg-green-50 dark:text-green-300 dark:border-green-700 dark:bg-green-900/30'
                      : wallet.balance < 0
                        ? 'text-red-700 border-red-200 bg-red-50 dark:text-red-300 dark:border-red-700 dark:bg-red-900/30'
                        : 'text-gray-600 border-gray-200 bg-gray-50 dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700/30'
                    }`}
                  style={{ textAlign: 'right' }}
                >
                  {formatCurrency(wallet.balance)}
                </div>
              )}
            </div>
          ))}
      </div>
      {/* Bottone Trasferimento */}
      {wallets.length > 1 && (
        <div className="mt-6">
          <button
            onClick={onShowTransferModal}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600/90 backdrop-blur-sm text-white rounded-xl shadow-lg hover:bg-purple-700/90 transition-all duration-200 transform hover:scale-105"
          >
            <ArrowRight className="w-4 h-4" />
            <span className="font-medium">Trasferisci tra Conti</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default WalletManager; 
